import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useNavigate } from 'react-router-dom'
import { useTheme } from '@mui/styles'
import { Divider, Stack, Typography, Skeleton, Paper, Tooltip } from '@mui/material'
import { getImageUrl } from 'services/assets'
import { Chip } from 'components/generic/chip'
import { dateTimeFormatter } from 'utils/data-manipulation/valueFormatter'
import { iconMapper } from 'components/containers/inbound-tickets/helpers/ticketActionsAndStatus'
import { useSelector } from 'react-redux'
import { trackEvent } from 'utils/tracking'
import { CHURN_ZERO_EVENTS, INBOUND_TICKET_FIELD_ARRAY } from 'utils/constants'
import { ImageNotSupported as ImageNotSupportedIcon } from '@mui/icons-material'
import { get } from 'lodash'
import { translateFieldValue } from './utils'

const imageContainerStyles = {
  overflow: 'hidden',
  borderRadius: 2,
  marginBottom: 2,
}

const imageStyles = {
  width: '100%',
  aspectRatio: '41/23',
  objectFit: 'cover',
}

const InboundTicketsGridCard = ({
  id,
  ticketLink,
  ticketId,
  status,
  ticketStatus,
  effectiveDate,
  customerIdentifierString,
  logoImage,
  gridImage,
  materialTypeCommonNames,
  ...props
}) => {
  const theme = useTheme()
  const navigate = useNavigate()
  const { defaultYardTimezone } = useSelector(state => state.auth)
  const { customizeIbtCardView } = useSelector(state => state.featureFlags?.variables || {})
  const fieldVisibilitySettings = useSelector(
    st => st.settings.settings.purchaseSettings.inboundTickets.fieldVisibility,
  )
  const [gridImageUrl, setGridImageUrl] = useState('')
  const [isLoadingMaterialImage, setIsLoadingMaterialImage] = useState(!!gridImage)
  const [hasImageError, setHasImageError] = useState(false)

  const visibleFields = INBOUND_TICKET_FIELD_ARRAY.filter(({ id }) => fieldVisibilitySettings.includes(id))

  const handleS3Error = error => {
    const errorMsg = error?.data?.error || 'There was an error getting the image'
    setHasImageError(true)
    trackEvent({ eventName: CHURN_ZERO_EVENTS.GET_S3_IMAGE, errorMsg })
  }

  useEffect(() => {
    if (gridImage) {
      getImageUrl(gridImage, logoImage)
        .then(url => {
          setGridImageUrl(url)
          setIsLoadingMaterialImage(false)
          setHasImageError(false)
        })
        .catch(handleS3Error)
    }
  }, [gridImage, logoImage])

  return (
    <Paper
      elevation={0}
      component="a"
      href={id ? ticketLink : undefined}
      onClick={e => {
        if (id) {
          e.preventDefault()
          setTimeout(() => navigate(ticketLink), 100)
        }
      }}
      sx={{
        border: 1,
        borderColor: 'borderColor.default',
        borderRadius: 1,
        backgroundColor: 'background.section',
        padding: 2,
        textDecoration: 'none',
        display: 'block',
        color: 'inherit',
        '&:hover': {
          boxShadow:
            '0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px rgba(0, 0, 0, 0.14), 0px 1px 5px rgba(0, 0, 0, 0.12)',
          backgroundColor: 'background.hover',
        },
        cursor: !id ? 'unset' : 'pointer',
      }}
    >
      <Stack direction="row" mb={0.5} mr={-1} justifyContent="space-between" alignItems="center">
        {!id ? (
          <Skeleton animation="wave" width="100%" />
        ) : (
          <>
            <Typography variant="h6">{ticketId ? `#${ticketId}` : ''}</Typography>
            <Chip
              icon={iconMapper[status?.value]}
              label={ticketStatus}
              sx={{
                fontSize: '14px',
                paddingLeft: 1,
                paddingRight: 0.5,
                '& .MuiChip-label': {
                  paddingLeft: 1,
                  paddingRight: 1,
                },
              }}
            />
          </>
        )}
      </Stack>
      {!id ? (
        <Skeleton animation="wave" width="100%" />
      ) : (
        <>
          <Tooltip title={customerIdentifierString || ''}>
            <Typography
              sx={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', maxWidth: '100%' }}
              variant="body2"
            >
              {customerIdentifierString || ' '}
            </Typography>
          </Tooltip>
        </>
      )}
      <Stack direction="row" mb={2} justifyContent="space-between" alignItems="center">
        {!id ? (
          <Skeleton animation="wave" width="100%" />
        ) : (
          <>
            <Typography variant="caption" whiteSpace="pre-wrap" color="textSecondary" lineHeight="normal">
              {effectiveDate
                ? `${dateTimeFormatter(effectiveDate, { reversed: true, timeZone: defaultYardTimezone })}`
                : ' '}
            </Typography>
          </>
        )}
        {!id ? (
          <Skeleton animation="wave" width="100%" />
        ) : (
          <>
            <Tooltip title={materialTypeCommonNames?.join(', ')}>
              <Typography
                variant="body2"
                whiteSpace="pre-wrap"
                sx={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', maxWidth: '40%' }}
              >
                {materialTypeCommonNames?.length > 1 ? 'Mixed' : materialTypeCommonNames?.[0] || 'No materials'}
              </Typography>
            </Tooltip>
          </>
        )}
      </Stack>
      {!id || isLoadingMaterialImage ? (
        <Skeleton
          sx={{
            ...imageStyles,
            ...imageContainerStyles,
            transform: 'none',
          }}
          animation="wave"
        />
      ) : (
        <Stack sx={{ ...imageStyles, ...imageContainerStyles, height: '100%' }}>
          {!ticketId || hasImageError ? (
            <Stack alignItems="center" justifyContent="center" height="100%" sx={{ color: 'text.hint' }}>
              <ImageNotSupportedIcon />
              <Typography fontSize="small">Error image.</Typography>
              <Typography fontSize="small">Please try again.</Typography>
            </Stack>
          ) : (
            <img
              src={gridImageUrl || logoImage || '/logo-vert-light.svg'}
              alt={`${ticketId}`}
              style={{
                ...imageStyles,
                objectFit: !gridImageUrl ? 'scale-down' : 'cover',
                backgroundColor: !gridImageUrl && !logoImage ? theme.palette.background.landing : 'unset',
              }}
            />
          )}
        </Stack>
      )}
      {customizeIbtCardView ? (
        <>
          {/* dynamically render fields based on visibility settings */}
          {visibleFields.map((field, index) => {
            // get the field value by using the id to traverse the props object
            const value = get(props, field.id)

            return (
              <React.Fragment key={field.id}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  {!id ? (
                    <Skeleton animation="wave" width="100%" />
                  ) : (
                    <>
                      <Typography variant="overline" whiteSpace="pre-wrap" lineHeight="normal" color="textSecondary">
                        {field.name}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', maxWidth: '75%' }}
                      >
                        {translateFieldValue(field.id, value)}
                      </Typography>
                    </>
                  )}
                </Stack>
                {index !== visibleFields.length - 1 && <Divider />}
              </React.Fragment>
            )
          })}
        </>
      ) : (
        <>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            {!id ? (
              <Skeleton animation="wave" width="100%" />
            ) : (
              <>
                <Typography variant="overline" whiteSpace="pre-wrap" lineHeight="normal" color="textSecondary">
                  Truck #
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', maxWidth: '75%' }}
                >
                  {props.truckNumber ? `#${props.truckNumber}` : 'None'}
                </Typography>
              </>
            )}
          </Stack>
          <Divider />
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            {!id ? (
              <Skeleton animation="wave" width="100%" />
            ) : (
              <>
                <Typography variant="overline" whiteSpace="nowrap" lineHeight="normal" color="textSecondary">
                  Note
                </Typography>
                <Tooltip title={props.notes?.[0]?.name || 'None'}>
                  <Typography
                    variant="body2"
                    sx={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', maxWidth: '75%' }}
                  >
                    {props.notes?.[0]?.name || 'None'}
                  </Typography>
                </Tooltip>
              </>
            )}
          </Stack>
        </>
      )}
    </Paper>
  )
}

InboundTicketsGridCard.propTypes = {
  id: PropTypes.string,
  ticketLink: PropTypes.string,
  ticketId: PropTypes.number,
  status: PropTypes.object,
  ticketStatus: PropTypes.string,
  effectiveDate: PropTypes.string,
  customerIdentifierString: PropTypes.string,
  truckNumber: PropTypes.string,
  logoImage: PropTypes.string,
  gridImage: PropTypes.string,
  materialTypeCommonNames: PropTypes.array,
  notes: PropTypes.array,
}

InboundTicketsGridCard.defaultProps = {
  id: '',
  ticketLink: '',
  ticketId: null,
  status: {},
  ticketStatus: '',
  effectiveDate: '',
  customerIdentifierString: '',
  truckNumber: '',
  logoImage: '',
  gridImage: '',
  materialTypeCommonNames: [],
  notes: [],
}

export default InboundTicketsGridCard
